import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Location,
  LocationInstrumentsWithFiltersRequest,
  LocationType,
} from "../../api/locationApi.ts";
import TextInputField from "../TextInputField.tsx";
import FormField from "../FormField.tsx";
import LocationRadioGroup from "../LocationRadioGroup.tsx";
import AddressSearch from "../location/AddressSearch.tsx";
import ActionButton from "../ActionButton.tsx";
import {
  getTranslation,
  useInstrumentsWithFilters,
} from "../../api/instrumentApi.ts";
import { t } from "@lingui/core/macro";
import { toast } from "react-hot-toast";
import { useLanguageStore } from "../../stores/languageStore.ts";
import TileSelector, { TileOption } from "../TileSelector.tsx";
import { Trans } from "@lingui/react/macro";

export interface LocationFormData {
  name: string;
  instruments: LocationInstrumentsWithFiltersRequest[];
  locationType: LocationType;
  selectedAddress: Location | null;
}

interface LocationFormProps {
  initialData: LocationFormData;
  onSubmit: (data: LocationFormData) => void;
  isSubmitting: boolean;
  submitLabel: string;
}

export default function LocationForm({
  initialData,
  onSubmit,
  isSubmitting,
  submitLabel,
}: LocationFormProps) {
  const [formData, setFormData] = useState(initialData);
  const [selectedInstrument, setSelectedInstrument] = useState("");

  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useInstrumentsWithFilters();

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const instrumentOptions = useMemo(() => {
    if (!supportedInstruments) return [];
    return supportedInstruments?.map((instrument) => ({
      value: instrument.name,
      label: getTranslation(instrument, language),
    }));
  }, [supportedInstruments, language]);

  const filterOptions = useMemo(() => {
    if (
      !formData.instruments?.find((value) => value.name === selectedInstrument)
    )
      return [];
    return (
      supportedInstruments
        ?.find((instrument) => instrument.name === selectedInstrument)
        ?.filters?.map((filter) => ({
          value: filter.name,
          label: getTranslation(filter, language),
        })) ?? []
    );
  }, [
    formData.instruments,
    supportedInstruments,
    selectedInstrument,
    language,
  ]);

  const selectedFilters = useMemo(() => {
    if (selectedInstrument == "" || !formData.instruments) return [];
    return (
      formData.instruments.find((value) => value.name === selectedInstrument)
        ?.filters ?? []
    );
  }, [selectedInstrument, formData.instruments]);

  const updateField = useCallback(
    (field: keyof LocationFormData, value: unknown) => {
      setFormData((prev: LocationFormData): LocationFormData => {
        return { ...prev, [field]: value };
      });
    },
    [],
  );

  const handleAddressSelect = useCallback(
    (address: Location) => {
      updateField("selectedAddress", address);
    },
    [updateField],
  );

  const onInstrumentUpdate = useCallback(
    (selections: string[]) => {
      const instrumentObjects: LocationInstrumentsWithFiltersRequest[] =
        selections.map((instrumentName) => {
          const existingInstrument = formData.instruments.find(
            (inst) => inst.name === instrumentName,
          );
          return (
            existingInstrument ?? {
              name: instrumentName,
              filters: [],
            }
          );
        });
      updateField("instruments", instrumentObjects);
      if (!selections.includes(selectedInstrument)) {
        setSelectedInstrument("");
      }
    },
    [formData.instruments, selectedInstrument, updateField],
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      if (formData.locationType == "REMOTE") {
        toast.error(
          t`Please select a valid address (atleast a city) and name.`,
        );
      } else {
        toast.error(
          t`Please select a valid address (incl. a street and housenumber) and name.`,
        );
      }
      return;
    }
    onSubmit(formData);
  };

  const selectedInstrumentOptions = useMemo(() => {
    if (!formData.instruments || !supportedInstruments) return [];
    return formData.instruments.map((instrument) => {
      const instrumentWithTranslations = supportedInstruments.find(
        (i) => i.name === instrument.name,
      );
      return {
        value: instrument.name,
        label: instrumentWithTranslations
          ? getTranslation(instrumentWithTranslations, language)
          : instrument.name,
        indicator: instrument.filters.length > 0,
      } as TileOption;
    });
  }, [formData.instruments, supportedInstruments, language]);

  const onFilterUpdate = useCallback(
    (selectedFilters: string[]) => {
      setFormData((prev) => {
        const updatedInstruments = prev.instruments.map((inst) => {
          if (inst.name === selectedInstrument) {
            return { ...inst, filters: selectedFilters };
          }
          return inst;
        });

        return { ...prev, instruments: updatedInstruments };
      });
    },
    [selectedInstrument],
  );

  const isFormValid = useMemo(() => {
    const nameValid = formData.name.trim().length > 0;
    const address = formData.selectedAddress;

    if (!nameValid || !address) return false;

    if (formData.locationType === "REMOTE") {
      return !!address.city;
    } else {
      return !!(address.streetName && address.houseNumber);
    }
  }, [formData.name, formData.locationType, formData.selectedAddress]);

  return (
    <form onSubmit={handleSubmit} className="md:max-w-full lg:max-w-fit">
      <TextInputField
        type="text"
        label={t`Location Name`}
        value={formData.name}
        onChange={(e) => updateField("name", e.target.value)}
        id="locationName"
      />

      <LocationRadioGroup
        selectedValue={formData.locationType}
        onChange={(type) => updateField("locationType", type)}
        label={t`Location Type`}
      />

      <AddressSearch
        selectedAddress={formData.selectedAddress}
        onAddressSelect={handleAddressSelect}
        label={t`Address`}
        placeholder={t`Search for an address...`}
        locationType={formData.locationType}
      />

      <FormField htmlFor="instruments" label={t`Instruments`}>
        <TileSelector
          options={instrumentOptions}
          selectedValues={formData.instruments.map(
            (instrument) => instrument.name,
          )}
          onChange={onInstrumentUpdate}
        />
      </FormField>

      <FormField htmlFor="filters" label={t`Filters`}>
        {formData.instruments.length > 0 ? (
          <>
            <TileSelector
              options={selectedInstrumentOptions}
              selectedValues={[selectedInstrument]}
              multiSelect={false}
              onChange={(newValues) =>
                setSelectedInstrument(newValues[0] ?? "")
              }
            />
            {selectedInstrument !== "" ? (
              filterOptions.length > 0 ? (
                <TileSelector
                  options={filterOptions}
                  selectedValues={selectedFilters}
                  onChange={onFilterUpdate}
                />
              ) : (
                <span className="mt-2 text-xs text-gray-600">
                  <Trans>No filters available for this instrument.</Trans>
                </span>
              )
            ) : (
              <span className="mt-2 text-xs text-gray-600">
                <Trans>Select an instrument to show possible filters.</Trans>
              </span>
            )}
          </>
        ) : (
          <span className={"mt-2 text-xs text-gray-600"}>
            <Trans>Select some instruments for this location.</Trans>
          </span>
        )}
      </FormField>

      <ActionButton
        className="mt-2"
        variant="primary"
        isLoading={isSubmitting}
        disabled={isSubmitting}
        type="submit"
      >
        {submitLabel}
      </ActionButton>
    </form>
  );
}
